import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { TrainingBanner } from "@/components/TrainingBanner";
import { Toast } from "@/components/common/Toast";
import { AIGeneratorSheet } from "@/components/sheet/AIGeneratorSheet";
import { PlaybookGenSheet } from "@/components/sheet/PlaybookGenSheet";
import { Landing } from "@/pages/build/Landing";
import { Configure } from "@/pages/build/Configure";
import { Monitor } from "@/pages/build/Monitor";
import { Done } from "@/pages/build/Done";
import { Preflight } from "@/pages/build/Preflight";
import { Runs } from "@/pages/runs/Runs";
import { RunDetail } from "@/pages/runs/RunDetail";
import { Templates } from "@/pages/templates/Templates";
import { Profiles } from "@/pages/profiles/Profiles";
import { PackEditor } from "@/pages/profiles/PackEditor";
import { Playbooks, PlaybookDetail } from "@/pages/playbooks/Playbooks";
import { Coach } from "@/pages/coach/Coach";
import { Explore } from "@/pages/explore/Explore";
import { Admin } from "@/pages/admin/Admin";
import { TrainingMonitor } from "@/pages/admin/TrainingMonitor";
import { Curator } from "@/pages/curator/Curator";
import { TeamLogin } from "@/pages/curator/TeamLogin";
import { Workshop } from "@/pages/admin/Workshop";
import { Docs } from "@/pages/docs/Docs";
import { CommandPalette } from "@/components/common/CommandPalette";
import { useAuth } from "@/hooks/useAuth";
import { useUiStore } from "@/lib/store/uiStore";

// Reads ?connected=1 / ?error=... from /oauth/callback's redirect-back
// and surfaces a toast. Refreshes auth state so the Nav chip flips to
// the connected org without a manual reload.
function PostAuthHandler(): null {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const showToast = useUiStore((s) => s.showToast);
  const { refetch } = useAuth();

  // Subscribe to the session-expired event raised by the HTTP client.
  // Earlier versions of v2 hard-redirected to /?error=session_expired
  // mid-job which destroyed every in-flight AI generation. Now we just
  // toast — the user can re-connect from the Nav menu without losing
  // the screen they were on.
  useEffect(() => {
    const handler = (): void => {
      showToast("Your session expired — please reconnect from the org menu.");
    };
    window.addEventListener("qb:session-expired", handler);
    return () => window.removeEventListener("qb:session-expired", handler);
  }, [showToast]);

  useEffect(() => {
    if (!search) return;
    const params = new URLSearchParams(search);
    const connected = params.get("connected");
    const err = params.get("error");
    if (!connected && !err) return;
    if (connected) {
      void refetch().then(() => {
        showToast("Connected to Salesforce ✓");
      });
    } else if (err) {
      const friendly =
        err === "session_expired"
          ? "Your session expired — please reconnect."
          : `Connection failed: ${err.replace(/_/g, " ")}`;
      showToast(friendly);
    }
    params.delete("connected");
    params.delete("error");
    const remaining = params.toString();
    navigate(`${pathname}${remaining ? `?${remaining}` : ""}`, { replace: true });
  }, [search, pathname, navigate, refetch, showToast]);

  return null;
}

export function App(): JSX.Element {
  return (
    <div className="min-h-full">
      <PostAuthHandler />
      <TrainingBanner />
      <Nav />
      <main>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/preflight" element={<Preflight />} />
          <Route path="/configure" element={<Configure />} />
          <Route path="/monitor/:runId" element={<Monitor />} />
          <Route path="/done/:runId" element={<Done />} />
          <Route path="/runs" element={<Runs />} />
          <Route path="/runs/:runId" element={<RunDetail />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/profiles" element={<Profiles />} />
          <Route path="/profiles/:id/edit" element={<PackEditor />} />
          <Route path="/playbooks" element={<Playbooks />} />
          <Route path="/playbooks/:id" element={<PlaybookDetail />} />
          <Route path="/coach" element={<Coach />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/training" element={<TrainingMonitor />} />
          <Route path="/admin/workshop" element={<Workshop />} />
          <Route path="/curator" element={<Curator />} />
          <Route path="/team-login" element={<TeamLogin />} />
          <Route path="/docs" element={<Docs />} />
          {/* Catch-all: anything unrecognized (e.g. stale /v2/* bookmarks
              from before the Apr 2026 cutover) bounces home instead of
              rendering an empty <main>. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Toast />
      <AIGeneratorSheet />
      <PlaybookGenSheet />
      <CommandPalette />
    </div>
  );
}
